// Outcome replay: entity states and terrain changes, not a physics simulation.
export const REPLAY_WIDTH = 80, REPLAY_HEIGHT = 24;
export const BOARD_WIDTH = 79, BOARD_HEIGHT = 20;
export const MAX_REPLAY_FRAMES = 12000, CHECKPOINT_INTERVAL = 120;
export const MAX_REPLAY_MS = 60 * 60 * 1000;
export const MAX_REPLAY_BYTES = 32 * 1024 * 1024;
export const MAX_COMPRESSED_BYTES = 8 * 1024 * 1024;
const cells = BOARD_WIDTH * BOARD_HEIGHT;
const integer = (n, min, max) => Number.isInteger(n) && n >= min && n <= max;
const ascii = text => typeof text === 'string' && /^[\x20-\x7e]*$/.test(text);
const label = text => typeof text === 'string' && text.length <= 120 && !/[\x00-\x1f\x7f]/.test(text);
const fail = () => { throw new Error('This is not a supported Ladder recording. Open a replay file, not a board or Series file.'); };

function entity(value, rock = false) {
    if (!value || !integer(value.x, -8, BOARD_WIDTH + 8) || !integer(value.y, -8, BOARD_HEIGHT + 8) ||
        !integer(value.state, 1, 13) || !(value.nextState === null || integer(value.nextState, 1, 13))) fail();
    const result = { x: value.x, y: value.y, state: value.state, nextState: value.nextState };
    for (const key of rock ? ['spawnStep', 'deathStep'] : ['jumpStep', 'deathStep']) {
        if (!integer(value[key], 0, 1000)) fail();
        result[key] = value[key];
    }
    // Out-of-board entities are safe only when dead (draw() omits dead rocks).
    if ((!rock || value.state !== 12) && (!integer(value.x, 0, BOARD_WIDTH - 1) || !integer(value.y, 0, BOARD_HEIGHT - 1))) fail();
    if (value.state === 11 && value.deathStep >= (rock ? 3 : 23)) fail();
    if (rock && value.state === 13 && value.spawnStep >= 9) fail();
    if (rock) { if (!integer(value.id, 1, Number.MAX_SAFE_INTEGER)) fail(); result.id = value.id; }
    return result;
}

function validateState(value) {
    if (!value || !integer(value.board, 1, Number.MAX_SAFE_INTEGER) || !label(value.name) ||
        !Array.isArray(value.rocks) || value.rocks.length > 2048) fail();
    const status = value.status;
    if (!status) fail();
    const cleanStatus = {};
    for (const key of ['score', 'lives', 'levelNumber']) {
        if (!integer(status[key], 0, Number.MAX_SAFE_INTEGER)) fail();
        cleanStatus[key] = status[key];
    }
    if (!integer(status.time, -100, 2000) || !integer(status.playSpeed, 1, 5)) fail();
    Object.assign(cleanStatus, { time: status.time, playSpeed: status.playSpeed });
    for (const key of ['paused', 'winning', 'testing', 'cheated']) {
        if (typeof status[key] !== 'boolean') fail();
        cleanStatus[key] = status[key];
    }
    if (![null, 'completed', 'failed'].includes(status.testResult)) fail();
    cleanStatus.testResult = status.testResult;
    const rocks = value.rocks.map(rock => entity(rock, true));
    if (new Set(rocks.map(rock => rock.id)).size !== rocks.length) fail();
    const result = { board: value.board, name: value.name, player: entity(value.player), rocks, status: cleanStatus };
    // Optional extension: older recordings still support viewing and basic practice.
    if (value.resume !== undefined) {
        const resume = value.resume;
        if (!resume || !integer(resume.difficulty, 0, 10000) || !integer(resume.nextLife, 1, Number.MAX_SAFE_INTEGER) ||
            !integer(resume.waitMs, 0, 1000)) fail();
        result.resume = { difficulty: resume.difficulty, nextLife: resume.nextLife, waitMs: resume.waitMs };
    }
    return result;
}

export function validateReplaySnapshot(snapshot) {
    if (!snapshot || !ascii(snapshot.layout) || snapshot.layout.length !== cells) fail();
    return { layout: snapshot.layout, state: validateState(snapshot.state) };
}

export class ReplayRecorder {
    constructor(title, { maxFrames = MAX_REPLAY_FRAMES } = {}) {
        if (!integer(maxFrames, 1, MAX_REPLAY_FRAMES)) throw new Error('No room for another recording. Download and remove an older recording first.');
        this.maxFrames = maxFrames;
        this.bytes = 1024;
        this.data = { format: 'ladder-state-replay', version: 1, renderer: 1,
            title: String(title).replace(/[\x00-\x1f\x7f]/g, '').slice(0, 120) || 'Ladder recording',
            createdAt: new Date().toISOString(), duration: 0, frames: [] };
    }
    capture(snapshot, now, actions = []) {
        if (this.stopped) return false;
        if (!Number.isFinite(now) || !ascii(snapshot.layout) || snapshot.layout.length !== cells) throw new Error('Invalid recording state');
        const state = validateState(snapshot.state);
        if (!Array.isArray(actions) || actions.length > 32 || actions.some(action => !integer(action, 11, 18))) throw new Error('Invalid recorded action');
        if (this.startedAt === undefined) this.startedAt = now;
        const time = Math.max(this.data.duration, Math.round(now - this.startedAt));
        if (time > MAX_REPLAY_MS) { this.finish(now, 'Recording time limit reached'); return false; }
        this.data.duration = time;
        // Timer phase is captured alongside changed states, not as a new frame on every draw.
        const signature = JSON.stringify({ ...state, resume: state.resume && { ...state.resume, waitMs: 0 } });
        if (this.previous && signature === this.previous.signature && snapshot.layout === this.previous.layout && !actions.length) return true;
        if (this.data.frames.length >= this.maxFrames) { this.finish(now, 'Recording capacity reached'); return false; }
        const frame = { at: time, state, actions: [...actions] };
        if (!this.previous || this.previous.board !== state.board || this.data.frames.length % CHECKPOINT_INTERVAL === 0) frame.layout = snapshot.layout;
        else {
            frame.changes = [];
            for (let i = 0; i < cells; i++) if (snapshot.layout[i] !== this.previous.layout[i]) frame.changes.push([i, snapshot.layout[i]]);
        }
        const bytes = new TextEncoder().encode(JSON.stringify(frame)).length + 1;
        if (this.bytes + bytes > MAX_REPLAY_BYTES) { this.finish(now, 'Recording size limit reached'); return false; }
        this.bytes += bytes;
        this.data.frames.push(frame);
        this.previous = { signature, layout: snapshot.layout, board: state.board };
        return true;
    }
    finish(now, reason = 'Recording stopped') {
        if (!this.stopped) {
            if (this.startedAt !== undefined && Number.isFinite(now)) this.data.duration = Math.min(MAX_REPLAY_MS, Math.max(this.data.duration, Math.round(now - this.startedAt)));
            this.data.reason = String(reason).replace(/[\x00-\x1f\x7f]/g, '').slice(0, 120);
            this.stopped = true;
        }
        return this.data.frames.length ? this.data : null;
    }
}

export function validateReplay(value) {
    if (!value || value.format !== 'ladder-state-replay' || value.version !== 1 || value.renderer !== 1) fail();
    if (!label(value.title) || !value.title.length || typeof value.createdAt !== 'string' || value.createdAt.length > 40 || !Number.isFinite(Date.parse(value.createdAt))) fail();
    if (!integer(value.duration, 0, MAX_REPLAY_MS) || !Array.isArray(value.frames) || !value.frames.length || value.frames.length > MAX_REPLAY_FRAMES) fail();
    let previousTime = -1, previousBoard, checkpoint = -1;
    const frames = value.frames.map((frame, index) => {
        if (!frame || !integer(frame.at, 0, value.duration) || frame.at < previousTime ||
            !Array.isArray(frame.actions) || frame.actions.length > 32 || frame.actions.some(action => !integer(action, 11, 18))) fail();
        previousTime = frame.at;
        const state = validateState(frame.state);
        const result = { at: frame.at, state, actions: [...frame.actions] };
        if (frame.layout !== undefined) {
            if (!ascii(frame.layout) || frame.layout.length !== cells || frame.changes !== undefined) fail();
            result.layout = frame.layout; checkpoint = index;
        } else {
            if (previousBoard !== state.board || checkpoint < 0 || index - checkpoint >= CHECKPOINT_INTERVAL ||
                !Array.isArray(frame.changes) || frame.changes.length > cells) fail();
            const seen = new Set();
            result.changes = frame.changes.map(change => {
                if (!Array.isArray(change) || change.length !== 2 || !integer(change[0], 0, cells - 1) ||
                    !ascii(change[1]) || change[1].length !== 1 || seen.has(change[0])) fail();
                seen.add(change[0]); return [...change];
            });
        }
        previousBoard = state.board;
        return result;
    });
    if (frames[0].at !== 0) fail();
    return { format: 'ladder-state-replay', version: 1, renderer: 1, title: value.title,
        createdAt: value.createdAt, duration: value.duration, frames,
        reason: label(value.reason) ? value.reason : 'Imported recording' };
}

// Count progression positions, not board instances recreated after a lost life.
export function replayDescription(replay) {
    const levels = new Set(replay.frames.map(frame => frame.state.status.levelNumber)).size;
    const score = replay.frames.at(-1).state.status.score;
    return `${replay.title} (${levels} level${levels === 1 ? '' : 's'}, ${score.toLocaleString()} ${score === 1 ? 'point' : 'points'})`;
}

// Derive markers from recorded outcomes, including older recordings.
export function replayTimelineEvents(replay) {
    const events = [];
    let previous, deathInProgress = false;
    replay.frames.forEach(({ state, at }, index) => {
        if (!previous || state.status.levelNumber !== previous.status.levelNumber) {
            events.push({ type: 'level', level: state.status.levelNumber + 1, index, at });
        }
        // Replay renderer v1 uses entity states 11 (dying) and 12 (dead).
        const dying = state.player.state === 11 || state.player.state === 12;
        const lostLife = previous && state.status.lives < previous.status.lives;
        if ((dying || lostLife) && !deathInProgress) events.push({ type: 'death', index, at });
        deathInProgress = dying;
        previous = state;
    });
    const index = replay.frames.length - 1, last = replay.frames[index];
    // Only mark a successful ending, never an intermediate campaign board.
    if (last && !deathInProgress && (last.state.status.winning || last.state.status.testResult === 'completed')) {
        events.push({ type: 'success', index, at: last.at });
    }
    return events;
}

export function replayFrameAt(replay, time) {
    let low = 0, high = replay.frames.length - 1;
    while (low < high) {
        const middle = Math.ceil((low + high) / 2);
        if (replay.frames[middle].at <= time) low = middle;
        else high = middle - 1;
    }
    return low;
}

export function replaySnapshotAt(replay, index) {
    if (!integer(index, 0, replay.frames.length - 1)) throw new Error('Invalid recording position');
    let start = index;
    while (start > 0 && replay.frames[start].layout === undefined) start--;
    const layout = [...replay.frames[start].layout];
    for (let i = start + 1; i <= index; i++) for (const [cell, char] of replay.frames[i].changes) layout[cell] = char;
    return { layout: layout.join(''), state: replay.frames[index].state };
}

// Bound decompression while reading, before collecting or parsing the JSON.
export async function readLimited(stream, limit) {
    const reader = stream.getReader(), chunks = [];
    let length = 0;
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            length += value.byteLength;
            if (length > limit) { await reader.cancel(); throw new Error('Recording exceeds the size limit.'); }
            chunks.push(value);
        }
    } finally { reader.releaseLock(); }
    const result = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) { result.set(chunk, offset); offset += chunk.length; }
    return result;
}

export async function decodeReplay(file) {
    const gzip = file.name.toLowerCase().endsWith('.json.gz');
    if (!gzip && !file.name.toLowerCase().endsWith('.json')) throw new Error('Choose a .ladder-replay.json.gz or .json recording.');
    if (file.size > (gzip ? MAX_COMPRESSED_BYTES : MAX_REPLAY_BYTES)) throw new Error('Recording exceeds the size limit.');
    if (gzip && typeof DecompressionStream === 'undefined') throw new Error('This browser cannot open compressed recordings. Use a current browser or an uncompressed JSON recording.');
    try {
        const stream = gzip ? file.stream().pipeThrough(new DecompressionStream('gzip')) : file.stream();
        const data = await readLimited(stream, MAX_REPLAY_BYTES);
        return validateReplay(JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(data)));
    } catch (error) {
        if (error.message.includes('size limit') || error.message.includes('supported Ladder')) throw error;
        throw new Error('The recording could not be read. It may be damaged or use the wrong format.');
    }
}

export async function encodeReplay(replay) {
    const data = new Blob([JSON.stringify(validateReplay(replay))], { type: 'application/json' });
    if (data.size > MAX_REPLAY_BYTES) throw new Error('Recording exceeds the size limit.');
    if (typeof CompressionStream === 'undefined') return { blob: data, extension: '.ladder-replay.json' };
    const bytes = await readLimited(data.stream().pipeThrough(new CompressionStream('gzip')), MAX_COMPRESSED_BYTES);
    return { blob: new Blob([bytes], { type: 'application/gzip' }), extension: '.ladder-replay.json.gz' };
}
